// Implements: F-MCK-016, F-MCK-017, F-MCK-006, F-MCK-019, F-UIX-007
/**
 * The favourite star and the "more actions" menu of a card or a row (the mockup's `.star` and
 * `cardMenu`). Both names carry the mockup's name, so a list of them is not a list of identical
 * buttons for a screen reader. Pure presentation: the menu calls back, the container acts.
 *
 * Compartir, Renombrar, Transferir, Mover and Papelera are offered only when the server says the caller
 * may do each (`allowedActionsOf`, the rule the editor's "Más acciones" shares): anybody else would only
 * be refused.
 */

import type { ReactElement } from "react";

import type { IconName } from "../../../shared/design-system/icons";
import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { MenuEntry } from "../../../shared/ui/molecules/Menu";
import type { MockupSummary } from "../../../types/api";
import { allowedActionsOf } from "../model/allowedActions";
import type { MockupActions } from "./mockupActions";
import styles from "./MockupActionsMenu.module.css";

export interface MockupActionsMenuProps {
  readonly mockup: MockupSummary;
  readonly actions: MockupActions;
}

export function MockupActionsMenu({ mockup, actions }: MockupActionsMenuProps): ReactElement {
  const { t } = useTranslation();
  const item = (id: string, label: string, icon: IconName, onSelect: () => void): MenuEntry => ({
    kind: "item",
    id,
    label,
    icon,
    onSelect,
  });
  const { onDuplicate, onTransfer, onExport } = actions;
  const may = allowedActionsOf(mockup);
  // The order of mockup v3 (:2856-2865), keeping "Renombrar", which v3 does not draw in the card menu.
  const entries: readonly MenuEntry[] = [
    item("open", t("gallery.card.open"), "canvas", () => {
      actions.onOpen(mockup);
    }),
    ...(onDuplicate === undefined
      ? []
      : [
          item("duplicate", t("mockups.menu.duplicate"), "copy", () => {
            onDuplicate(mockup);
          }),
        ]),
    ...(may.share
      ? [
          item("share", t("gallery.card.share"), "link", () => {
            actions.onShare(mockup);
          }),
        ]
      : []),
    ...(may.rename
      ? [
          item("rename", t("gallery.card.rename"), "tag", () => {
            actions.onRename(mockup);
          }),
        ]
      : []),
    ...(onTransfer === undefined || !may.transfer
      ? []
      : [
          item("transfer", t("mockups.menu.transfer"), "users", () => {
            onTransfer(mockup);
          }),
        ]),
    ...(may.move
      ? [
          item("move", t("gallery.card.move"), "folder", () => {
            actions.onMove(mockup);
          }),
        ]
      : []),
    ...(onExport === undefined
      ? []
      : ([
          { kind: "separator", id: "export-separator" },
          { kind: "label", id: "export", label: t("mockups.menu.exportGroup") },
          item("export-html", t("mockups.menu.exportHtml"), "download", () => {
            onExport(mockup, "html");
          }),
          item("export-zip", t("mockups.menu.exportZip"), "download", () => {
            onExport(mockup, "zip");
          }),
          item("export-diagram", t("mockups.menu.exportDiagram"), "diagram", () => {
            onExport(mockup, "diagram");
          }),
        ] satisfies MenuEntry[])),
    ...(may.trash
      ? ([
          { kind: "separator", id: "separator" },
          {
            kind: "item",
            id: "trash",
            label: t("gallery.card.trash"),
            icon: "trash",
            danger: true,
            onSelect: () => {
              actions.onTrash(mockup);
            },
          },
        ] satisfies MenuEntry[])
      : []),
  ];
  const favoriteLabel = `${mockup.is_favorite ? t("gallery.favorite.remove") : t("gallery.favorite.add")}: ${mockup.name}`;
  const menuLabel = t("gallery.card.menu", { name: mockup.name });

  return (
    <span className={styles.actions}>
      <span className={styles.star}>
        <Button
          variant="ghost"
          iconOnly
          label={favoriteLabel}
          pressed={mockup.is_favorite}
          onClick={() => {
            actions.onToggleFavorite(mockup);
          }}
        >
          <Icon name="star" />
        </Button>
      </span>
      <Menu
        label={menuLabel}
        align="end"
        entries={entries}
        renderTrigger={(trigger) => (
          <button type="button" {...trigger} className={buttonClassName("ghost", "md", true)} aria-label={menuLabel}>
            <Icon name="more" />
          </button>
        )}
      />
    </span>
  );
}
